import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  lastValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { VipLevelConfig, VipLevelUpClaim } from '@gains-com/shared';
import { environment } from '../../../environments/environment.js';
import { UserViewService } from './user-view.service.js';

/**
 * Claimable VIP weekly reward. Mirrors the `WeeklyRewardStatus` shape
 * returned by `GET /self/vip/weekly-reward`. Money fields are 6dp decimal
 * strings; timestamps are ISO. `forfeit_at` is when the reward is lost if
 * unclaimed.
 */
export interface VipWeeklyReward {
  vip_weekly_reward_id: string;
  reward_period_start: string;
  flat_sc: string;
  flat_gc: string;
  loss_rebate_sc: string;
  net_losses_sc: string;
  /** flat_sc + loss_rebate_sc */
  total_sc: string;
  total_gc: string;
  forfeit_at: string;
}

/**
 * Response shape of `GET /self/vip/weekly-reward`. The server always sends
 * `next_issue_at`; it is nullable here so a failed fetch degrades to the same
 * shape rather than erroring the combined state.
 */
interface VipWeeklyRewardStatus {
  reward: VipWeeklyReward | null;
  /** Next Monday 13:00 America/New_York — when the next bonus is issued. */
  next_issue_at: string | null;
}

interface VipViewState {
  /** All active VIP level configs (full ladder, ordered by points_required). */
  configs: VipLevelConfig[];
  /** Unclaimed level-up rewards for the current user. */
  unclaimedLevelRewards: VipLevelUpClaim[];
  /** Current claimable weekly reward, or null when nothing is pending. */
  weeklyReward: VipWeeklyReward | null;
  /**
   * When the next weekly bonus is issued (ISO). Server-computed so the DST-aware
   * Eastern boundary lives in one place. Null only if the fetch failed.
   */
  weeklyNextIssueAt: string | null;
}

/**
 * View-state service for the VIP page.
 *
 * Mirrors {@link PromotionsView}: a per-user state `signal` re-`set` whenever
 * the authenticated user changes, exposed as a `shareReplay(1)` stream so every
 * consumer shares a single fetch. The configs endpoint is public, so it is
 * fetched once and merged with the per-user reward streams.
 *
 * Claims (`claimLevelReward`, `claimWeeklyReward`) refresh the relevant slice of
 * state and push the resulting balances back through {@link UserViewService}
 * (mirrors the existing rakeback / promotions `syncBalances()` pattern).
 */
@Injectable({
  providedIn: 'root',
})
export class VipViewService {
  private readonly _userView = inject(UserViewService);
  private readonly _http = inject(HttpClient);

  /**
   * VIP level configs. Public endpoint, fetched once and shared. Unlike the
   * legacy inline call this keeps the full ladder (does NOT pre-filter on
   * `points_required > 0`) so the tiers grid and the banner colors can resolve
   * the base level too; the component filters where a filtered view is needed.
   */
  readonly configs$: Observable<VipLevelConfig[]> = this._http
    .get<VipLevelConfig[]>(`${environment.apiUrl}/metadata/vip-configs`)
    .pipe(take(1), shareReplay(1));

  // Single per-service signal, `.set(null)` / `.set(state)` on user change
  // (was a BehaviorSubject reassigned per user). Mirrors {@link PromotionsView}.
  private readonly _state = signal<VipViewState | null>(null);

  /** Readonly signal surface for new consumers. */
  readonly state = this._state.asReadonly();

  /** Back-compat stream shim sourcing the signal for the observable pipeline below. */
  private readonly _state$ = toObservable(this._state);

  /**
   * Combined VIP view state. Rebuilt when the user id / pii flag changes; null
   * before the user has submitted pii (the reward endpoints require it).
   */
  readonly state$: Observable<VipViewState | null> = this._userView.state$.pipe(
    distinctUntilChanged(
      (u1, u2) =>
        u1?.user_id === u2?.user_id &&
        u1?.pii_submitted === u2?.pii_submitted &&
        u1?.vip_enabled === u2?.vip_enabled,
    ),
    switchMap((user) => {
      // Null when signed out, PII not yet submitted, or the per-user vip
      // kill-switch is explicitly off. null/undefined (never toggled) still
      // means enabled — mirrors killSwitchGuard/the claim-service checks.
      if (!user || !user.pii_submitted || user.vip_enabled === false) {
        this._state.set(null);

        return of(null);
      }

      return combineLatest([this.configs$, this._fetchUnclaimedLevelRewards$(), this._fetchWeeklyReward$()]).pipe(
        switchMap(([configs, unclaimedLevelRewards, weeklyStatus]) => {
          this._state.set({
            configs,
            unclaimedLevelRewards,
            weeklyReward: weeklyStatus.reward,
            weeklyNextIssueAt: weeklyStatus.next_issue_at,
          });

          return this._state$;
        }),
      );
    }),
    shareReplay(1),
  );

  /** Unclaimed level-up rewards, derived from the combined state. */
  readonly unclaimedLevelRewards$: Observable<VipLevelUpClaim[]> = this.state$.pipe(
    map((state) => state?.unclaimedLevelRewards ?? []),
  );

  /** Current claimable weekly reward, derived from the combined state. */
  readonly weeklyReward$: Observable<VipWeeklyReward | null> = this.state$.pipe(
    map((state) => state?.weeklyReward ?? null),
  );

  /** When the next weekly bonus is issued, derived from the combined state. */
  readonly weeklyNextIssueAt$: Observable<string | null> = this.state$.pipe(
    map((state) => state?.weeklyNextIssueAt ?? null),
  );

  /**
   * Count of unclaimed VIP rewards driving the claim-notification badge:
   * unclaimed level-up rewards + 1 if a weekly reward is pending. Monthly
   * bonus (cut, no grant path) and VIP promo (not a distinct claim type —
   * generic Promotions has its own count) are excluded.
   */
  readonly unclaimedVipClaimCount$: Observable<number> = this.state$.pipe(
    map((state) => (state?.unclaimedLevelRewards.length ?? 0) + (state?.weeklyReward ? 1 : 0)),
  );

  // A failed optional reward fetch must not error the combined `state$` (that
  // would throw on every signal read and crash the whole VIP page). Degrade to a
  // safe empty value and let the rest of the page render.
  private _fetchUnclaimedLevelRewards$ = () =>
    this._http.get<VipLevelUpClaim[]>(`${environment.apiUrl}/self/vip/unclaimed-level-rewards`).pipe(
      take(1),
      catchError(() => of<VipLevelUpClaim[]>([])),
    );

  private _fetchWeeklyReward$ = () =>
    this._http.get<VipWeeklyRewardStatus>(`${environment.apiUrl}/self/vip/weekly-reward`).pipe(
      take(1),
      map((res) => ({ reward: res.reward ?? null, next_issue_at: res.next_issue_at ?? null })),
      catchError(() => of<VipWeeklyRewardStatus>({ reward: null, next_issue_at: null })),
    );

  /**
   * Claims a single VIP level-up reward and drops it from local state. The
   * server credits SC/GC; we resync balances through {@link UserViewService}
   * so the header balance reflects the credit. Throws on failure (the caller
   * surfaces the error code via the dialog / alert).
   */
  async claimLevelReward(vipLevelUpClaimId: string): Promise<void> {
    await lastValueFrom(
      this._http
        .post(`${environment.apiUrl}/self/vip/claim-level-reward`, {
          vip_level_up_claim_id: vipLevelUpClaimId,
        })
        .pipe(take(1)),
    );

    const current = this._state();
    if (current) {
      this._state.set({
        ...current,
        unclaimedLevelRewards: current.unclaimedLevelRewards.filter(
          (claim) => claim.vip_level_up_claim_id !== vipLevelUpClaimId,
        ),
      });
    }

    await this._userView.syncBalances();
  }

  /**
   * Claims the current weekly reward, clears it from local state and resyncs
   * balances. Throws on failure (`NO_REWARD_AVAILABLE`, `ALREADY_CLAIMED`,
   * `VIP_FEATURE_DISABLED`).
   */
  async claimWeeklyReward(): Promise<void> {
    try {
      await lastValueFrom(this._http.post(`${environment.apiUrl}/self/vip/weekly-reward`, {}).pipe(take(1)));
    } catch (err: unknown) {
      // A VIP_FEATURE_DISABLED 403 is the authoritative kill-switch signal: this
      // session's cached vip_enabled has gone stale (the kill-switch was applied
      // without a realtime UserFeatureUpdated reaching this client). Reflect it so
      // the VIP surface re-gates — state$ nulls, the page redirects, the claim
      // button disappears — instead of the user retrying into the 403 wall.
      if ((err as { error?: { code?: string } })?.error?.code === 'VIP_FEATURE_DISABLED') {
        this._userView.updateUser({ vip_enabled: false });
      }
      throw err;
    }

    const current = this._state();
    if (current) {
      this._state.set({ ...current, weeklyReward: null });
    }

    await this._userView.syncBalances();
  }

  /**
   * Clears the weekly reward from local state in response to a
   * `vip_weekly_reward_claimed` realtime event. Backstops the same-session
   * optimistic clear in {@link claimWeeklyReward} and, more importantly, flips
   * the card for a VIP page open in another tab/device when the claim happened
   * elsewhere. No-ops when the VIP page isn't open (`_state` null) or the
   * pending reward is a different one.
   */
  weeklyRewardClaimed(vipWeeklyRewardId: string): void {
    const current = this._state();
    if (!current || current.weeklyReward?.vip_weekly_reward_id !== vipWeeklyRewardId) {
      return;
    }

    this._state.set({ ...current, weeklyReward: null });
  }

  /** Refetches the unclaimed level rewards + weekly reward from the server. */
  async refresh(): Promise<void> {
    const snapshot = this._state();
    if (!snapshot) {
      return;
    }

    const [unclaimedLevelRewards, weeklyStatus] = await Promise.all([
      lastValueFrom(this._fetchUnclaimedLevelRewards$()),
      lastValueFrom(this._fetchWeeklyReward$()),
    ]);

    // The user may have changed mid-await, in which case `state$` re-`set` the
    // signal to a fresh (or null) state object — don't push stale data. The
    // captured object reference is replaced on every user change, so an identity
    // mismatch reproduces the old per-subject swap guard.
    if (this._state() !== snapshot) {
      return;
    }

    this._state.set({
      ...snapshot,
      unclaimedLevelRewards,
      weeklyReward: weeklyStatus.reward,
      weeklyNextIssueAt: weeklyStatus.next_issue_at,
    });
  }
}
